import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/http';
import { useToasts } from '../hooks/useToasts';

const buttonStyle = {
  padding: '8px 16px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
};

const primaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--primary-color)',
  color: 'white',
};

const secondaryButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--secondary-button-bg)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-color)',
};

const closeIconButtonStyle = {
  ...buttonStyle,
  marginLeft: '12px',
  padding: '4px 8px',
  backgroundColor: 'transparent',
  border: 'none',
  color: 'var(--text-secondary)',
  fontSize: '20px',
  lineHeight: 1,
};

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
  };

  const contentStyle = {
    position: 'relative',
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};

const EmployeeDetailModal = ({ isOpen, onClose, employeeId, onUpdate, canView = true, category = 'main' }) => {
  const { showSuccess, showError } = useToasts();
  const [employee, setEmployee] = useState(null);
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [view, setView] = useState('details'); // 'details' or 'history'
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailError, setDetailError] = useState(null);
  
  // 组织元数据相关状态 - 用于级联加载
  const [orgMetaCache, setOrgMetaCache] = useState({
    department: [],
    section: [],
    team: [],
    position: []
  });
  const [deptOptions, setDeptOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [positionOptions, setPositionOptions] = useState([]);

  const fieldNameMap = {
    name: '姓名',
    id_card_number: '身份证号码',
    gender: '性别',
    ethnicity: '民族',
    department: '工作部门',
    section: '科室(工序)',
    team: '班组',
    position: '工作岗位',
    birth_date: '出生年月',
    political_status: '政治面貌',
    education: '学历',
    marital_status: '婚姻状况',
    hometown: '籍贯',
    address: '家庭住址',
    phone: '联系方式',
    email: '邮箱',
    hire_date: '入职日期',
    contract_due_date: '合同到期日',
    contract_end_date: '合同终止日期',
    status: '员工状态',
    on_the_job_medical_exam_date: '在岗体检时间',
    has_occupational_disease_history: '有无职业病史',
    occupational_disease_history_description: '职业病史描述',
  };

  useEffect(() => {
    if (isOpen && employeeId) {
      setView('details');
      setIsEditing(false);
      setEmployee(null);
      setFormData({});
      setErrors({});
      setAuditLogs([]);
      setDetailError(null);
      setIsLoadingDetails(true);
      // 使用正确的API路径格式获取员工信息，并传递category参数
      axios.get(`/employees/${employeeId}`, { params: { category } })
        .then(response => {
          const fetchedEmployee = response.data;
          setEmployee(fetchedEmployee);
          setFormData(fetchedEmployee);
        })
        .catch(error => {
          console.error('获取员工详细信息失败:', error);
          setDetailError(error.friendlyMessage || error.message || '加载失败');
          showError(`获取详细信息失败: ${error.friendlyMessage || error.message}`);
        })
        .finally(() => setIsLoadingDetails(false));

      // 使用正确的API路径格式获取变更历史，并传递category参数
      axios.get(`/employees/${employeeId}/logs`, { params: { category } })
        .then(response => setAuditLogs(response.data || []))
        .catch(error => {
          console.error('获取变更历史失败:', error);
          showError(`获取变更历史失败: ${error.friendlyMessage || error.message}`);
        });
    } else if (!isOpen) {
      setIsEditing(false);
      setEmployee(null);
      setFormData({});
      setErrors({});
      setAuditLogs([]);
      setIsLoadingDetails(false);
      setDetailError(null);
    }
  }, [isOpen, employeeId, category, showError]);

  // 根据身份证号码计算年龄和出生日期
  const calculateAgeFromIdCard = (idCardNumber) => {
    if (!idCardNumber || idCardNumber.length < 17) return { age: null, birthDate: null };
    
    let birthYear, birthMonth, birthDay;
    // 处理18位身份证
    if (idCardNumber.length === 18) {
      birthYear = idCardNumber.substring(6, 10);
      birthMonth = idCardNumber.substring(10, 12);
      birthDay = idCardNumber.substring(12, 14);
    } 
    // 处理15位身份证
    else if (idCardNumber.length === 15) {
      birthYear = '19' + idCardNumber.substring(6, 8);
      birthMonth = idCardNumber.substring(8, 10);
      birthDay = idCardNumber.substring(10, 12);
    }
    
    if (birthYear && birthMonth && birthDay) {
      const birthDate = new Date(birthYear, birthMonth - 1, birthDay);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      
      // 调整年龄计算，考虑生日是否已过
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      // 格式化出生日期为YYYY-MM-DD
      const formattedBirthDate = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;
      return { age, birthDate: formattedBirthDate };
    }
    
    return { age: null, birthDate: null };
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = '姓名不能为空';
    if (!formData.id_card_number) newErrors.id_card_number = '身份证号码不能为空';
    
    if (!formData.phone) {
      newErrors.phone = '联系方式不能为空';
    } else if (!/^\d{11}$/.test(formData.phone)) {
      newErrors.phone = '请输入有效的11位手机号码';
    }

    // 邮箱设为可空，但如果填写了则进行格式验证
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '请输入有效的邮箱地址';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // 如果是身份证号码变更，自动计算年龄和出生日期
    if (name === 'id_card_number') {
      const { age, birthDate } = calculateAgeFromIdCard(value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        age: age,
        birth_date: birthDate || prev.birth_date
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(employee); // Revert changes
    setErrors({});
  };

  const handleUpdate = () => {
    if (!validate()) {
      return;
    }
    // Convert empty date strings to null before sending
    const dataToSend = { ...formData };
    for (const key in dataToSend) {
      if (key.includes('date') && dataToSend[key] === '') {
        dataToSend[key] = null;
      }
    }

    // 使用正确的API路径格式更新员工信息
    axios.put(`/employees/${employeeId}`, dataToSend, { params: { category } })
      .then(() => {
        showSuccess('员工信息更新成功！');
        setIsEditing(false);
        if (onUpdate) {
          onUpdate(); // Refresh the employee list
        }
        // Refetch data to show updated info
        axios.get(`/employees/${employeeId}`, { params: { category } }).then(res => {
            setEmployee(res.data);
            setFormData(res.data);
        });
      })
      .catch(error => {
        console.error('更新员工信息失败:', error);
        showError(`更新失败: ${error.friendlyMessage || error.message}`);
      });
  };
  
  // 加载部门数据
  const loadDepartments = useCallback(async () => {
    try {
      if (orgMetaCache.department.length > 0) {
        setDeptOptions(orgMetaCache.department.map(d => ({ label: d.name, value: d.name, id: d.id })));
        return orgMetaCache.department;
      }
      const { data } = await axios.get('/org-meta', { params: { type: 'department', enabled_only: '1' } });
      const departments = Array.isArray(data) ? data : [];
      setOrgMetaCache(prev => ({ ...prev, department: departments }));
      setDeptOptions(departments.map(d => ({ label: d.name, value: d.name, id: d.id })));
      return departments;
    } catch (e) {
      console.error('加载部门数据失败', e);
      setDeptOptions([]);
      return [];
    }
  }, [orgMetaCache.department]);
  
  // 级联加载科室数据
  const loadSections = useCallback(async (departmentName, options = {}) => {
    const { resetLower = true } = options;
    if (!departmentName) {
      setSectionOptions([]);
      if (resetLower) {
        setTeamOptions([]);
        setPositionOptions([]);
      }
      return [];
    }
    try {
      let departments = orgMetaCache.department;
      if (!departments.length) {
        departments = await loadDepartments();
      }
      const dept = (departments || []).find(d => d.name === departmentName);
      if (!dept) {
        setSectionOptions([]);
        if (resetLower) {
          setTeamOptions([]);
          setPositionOptions([]);
        }
        return [];
      }
      const { data } = await axios.get('/org-meta', { params: { type: 'section', parent_id: dept.id, enabled_only: '1' } });
      const sections = Array.isArray(data) ? data : [];
      setOrgMetaCache(prev => ({ ...prev, section: sections }));
      setSectionOptions(sections.map(s => ({ label: s.name, value: s.name, id: s.id })));
      if (resetLower) {
        setTeamOptions([]);
        setPositionOptions([]);
      }
      return sections;
    } catch (e) {
      console.error('加载科室数据失败', e);
    }
    setSectionOptions([]);
    if (resetLower) {
      setTeamOptions([]);
      setPositionOptions([]);
    }
    return [];
  }, [orgMetaCache.department, loadDepartments]);
  
  // 级联加载班组数据
  const loadTeams = useCallback(async (sectionName, options = {}) => {
    const { resetLower = true, sectionId, departmentName } = options;
    if (!sectionName && !sectionId) {
      setTeamOptions([]);
      if (resetLower) setPositionOptions([]);
      return [];
    }
    try {
      let targetSectionId = sectionId || null;
      let sectionList = orgMetaCache.section;
      if (!targetSectionId) {
        if (!sectionList.length || !sectionList.some(s => s.name === sectionName)) {
          const deptName = departmentName || formData.department || '';
          if (deptName) {
            sectionList = await loadSections(deptName, { resetLower: false });
          }
        }
        const matchedSection = (sectionList || []).find(s => s.name === sectionName);
        targetSectionId = matchedSection?.id;
      }
      if (!targetSectionId) {
        setTeamOptions([]);
        if (resetLower) setPositionOptions([]);
        return [];
      }
      const { data } = await axios.get('/org-meta', { params: { type: 'team', parent_id: targetSectionId, enabled_only: '1' } });
      const teams = Array.isArray(data) ? data : [];
      setOrgMetaCache(prev => ({ ...prev, team: teams }));
      setTeamOptions(teams.map(t => ({ label: t.name, value: t.name, id: t.id })));
      if (resetLower) setPositionOptions([]);
      return teams;
    } catch (e) {
      console.error('加载班组数据失败', e);
    }
    setTeamOptions([]);
    if (resetLower) setPositionOptions([]);
    return [];
  }, [orgMetaCache.section, formData.department, loadSections]);
  
  // 级联加载岗位数据
  const loadPositions = useCallback(async (teamName, options = {}) => {
    const { teamId, sectionName } = options;
    const parentIds = [];

    let targetTeamId = teamId || null;
    let teamList = orgMetaCache.team;
    if (!targetTeamId && teamName) {
      if (!teamList.length || !teamList.some(t => t.name === teamName)) {
        const secName = sectionName || formData.section || '';
        if (secName) {
          teamList = await loadTeams(secName, { resetLower: false });
        }
      }
      const matchedTeam = (teamList || []).find(t => t.name === teamName);
      targetTeamId = matchedTeam?.id || null;
    }
    if (targetTeamId) {
      parentIds.push(targetTeamId);
    }

    if (!parentIds.length) {
      const targetSectionName = sectionName || formData.section || '';
      if (targetSectionName) {
        let sections = orgMetaCache.section;
        if (!sections.length || !sections.some(s => s.name === targetSectionName)) {
          const deptName = formData.department || '';
          if (deptName) {
            sections = await loadSections(deptName, { resetLower: false });
          }
        }
        const matchedSection = (sections || []).find(s => s.name === targetSectionName);
        if (matchedSection?.id) {
          parentIds.push(matchedSection.id);
        }
      }
    }

    if (!parentIds.length) {
      setPositionOptions([]);
      return [];
    }

    try {
      const aggregated = [];
      for (const pid of parentIds) {
        const { data } = await axios.get('/org-meta', { params: { type: 'position', parent_id: pid, enabled_only: '1' } });
        if (Array.isArray(data)) {
          aggregated.push(...data);
        }
      }
      const deduped = [];
      const seen = new Set();
      aggregated.forEach(item => {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          deduped.push(item);
        }
      });
      setOrgMetaCache(prev => ({ ...prev, position: deduped }));
      setPositionOptions(deduped.map(p => ({ label: p.name, value: p.name, id: p.id })));
      return deduped;
    } catch (e) {
      console.error('加载岗位数据失败', e);
      setPositionOptions([]);
      return [];
    }
  }, [orgMetaCache.team, orgMetaCache.section, formData.section, formData.department, loadTeams, loadSections]);
  
  // 自动填充组织信息
  const autoFillOrgInfo = useCallback(async (employeeData) => {
    if (!employeeData) return;
    if (!employeeData.department) return;

    await loadDepartments();
    const sections = await loadSections(employeeData.department, { resetLower: false });
    if (employeeData.section) {
      const matchedSection = (sections || []).find(s => s.name === employeeData.section);
      const teams = await loadTeams(employeeData.section, {
        resetLower: false,
        sectionId: matchedSection?.id,
        departmentName: employeeData.department,
      });
      if (employeeData.team) {
        const matchedTeam = (teams || []).find(t => t.name === employeeData.team);
        await loadPositions(employeeData.team, {
          teamId: matchedTeam?.id,
          sectionName: employeeData.section,
        });
      }
    }
  }, [loadDepartments, loadSections, loadTeams, loadPositions]);

  const handleModify = useCallback(async () => {
    setIsEditing(true);
    setErrors({});
    await loadDepartments();
    if (employee) {
      await autoFillOrgInfo(employee);
    }
  }, [autoFillOrgInfo, employee, loadDepartments]);
  
  // 处理部门选择变化
  const handleDepartmentChange = (e) => {
    const department = e.target.value;
    setFormData(prev => ({ 
      ...prev, 
      department,
      section: '',
      team: '',
      position: ''
    }));
    
    if (department) {
      loadSections(department, { resetLower: true });
    } else {
      setSectionOptions([]);
      setTeamOptions([]);
      setPositionOptions([]);
    }
  };
  
  // 处理科室选择变化
  const handleSectionChange = (e) => {
    const section = e.target.value;
    const currentDepartment = formData.department;
    setFormData(prev => ({ 
      ...prev, 
      section,
      team: '',
      position: ''
    }));
    
    if (section) {
      loadTeams(section, { departmentName: currentDepartment, resetLower: true });
    } else {
      setTeamOptions([]);
      setPositionOptions([]);
    }
  };
  
  // 处理班组选择变化
  const handleTeamChange = (e) => {
    const team = e.target.value;
    const currentSection = formData.section;
    setFormData(prev => ({ 
      ...prev, 
      team,
      position: ''
    }));
    
    if (team) {
      loadPositions(team, { sectionName: currentSection });
    } else {
      setPositionOptions([]);
    }
  };
  
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        // Check if the date is valid
        if (isNaN(date.getTime())) {
            return '';
        }
        // Format to YYYY-MM-DD
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch (e) {
        return ''; // Return empty if date is invalid
    }
  };

  const renderHistoryView = () => {
    const historyTableStyle = {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '1rem',
    };
    const thStyle = {
      border: '1px solid var(--border-color)',
      padding: '12px',
      textAlign: 'left',
      backgroundColor: 'var(--bg-secondary)',
      color: 'var(--text-primary)',
    };
    const tdStyle = {
      border: '1px solid var(--border-color)',
      padding: '12px',
      color: 'var(--text-secondary)',
      wordBreak: 'break-all',
    };

    return (
      <div>
        <h4 style={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>变更历史</h4>
        {auditLogs.length === 0 ? (
          <p>暂无变更记录。</p>
        ) : (
          <table style={historyTableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>变更时间</th>
                <th style={thStyle}>变更内容</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.map(log => {
                const changedFields = JSON.parse(log.changed_fields || '[]');
                const oldValues = JSON.parse(log.old_values || '{}');
                const newValues = JSON.parse(log.new_values || '{}');
                return (
                  <tr key={log.id}>
                    <td style={tdStyle}>{new Date(log.changed_at).toLocaleString()}</td>
                    <td style={tdStyle}>
                      {changedFields.map(field => (
                        <div key={field} style={{ marginBottom: '8px' }}>
                          <strong>{fieldNameMap[field] || field}:</strong>
                          <br />
                          <span style={{ color: '#f87171' }}>旧: {oldValues[field] || '空'}</span>
                          <br />
                          <span style={{ color: '#4ade80' }}>新: {newValues[field] || '空'}</span>
                        </div>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const formRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  };

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
  };

  const labelStyle = {
    marginBottom: '0.5rem',
    color: 'var(--text-secondary)',
    fontSize: '14px',
  };

  const inputStyle = {
    padding: '0.75rem',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    backgroundColor: isEditing ? 'white' : 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '1rem',
  };

  const headerActionsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  };

  const errorStyle = {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '4px',
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div style={headerStyle}>
        <h3 style={{ color: 'var(--text-primary)', margin: 0 }}>
          {view === 'details' ? '人员详细信息' : '变更历史'}
        </h3>
        <div style={headerActionsStyle}>
          {view === 'details' ? (
            <>
              <button style={secondaryButtonStyle} onClick={() => setView('history')}>查看历史</button>
              {canView && !isEditing && (
                <button style={primaryButtonStyle} onClick={handleModify}>修改</button>
              )}
              {canView && isEditing && (
                <>
                  <button style={primaryButtonStyle} onClick={handleUpdate}>保存</button>
                  <button style={secondaryButtonStyle} onClick={handleCancel}>取消</button>
                </>
              )}
            </>
          ) : (
            <button style={primaryButtonStyle} onClick={() => setView('details')}>返回详情</button>
          )}
          <button type="button" style={closeIconButtonStyle} onClick={onClose} aria-label="关闭" title="关闭">&times;</button>
        </div>
      </div>

      {!canView && !detailError && (
        <div style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          padding: '0.6rem 0.9rem',
          borderRadius: '6px',
          fontSize: '0.75rem',
          color: '#9a3412',
          marginBottom: '0.9rem'
        }}>
          当前账号缺少“查看/编辑员工详情”权限。已尝试加载可公开信息，编辑功能已禁用。
        </div>
      )}

      {detailError && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          padding: '0.75rem 1rem',
          borderRadius: '6px',
          color: '#b91c1c',
          fontSize: '0.85rem',
          marginBottom: '1rem'
        }}>加载失败：{detailError}</div>
      )}

      {isLoadingDetails && !employee && !detailError && (
        <div style={{ padding: '1rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>正在加载详细信息...</div>
      )}

      {view === 'details' && employee ? (
        <form>
          <div style={formRowStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>姓名</label>
              <input style={inputStyle} type="text" name="name" value={formData.name || ''} onChange={handleInputChange} readOnly={!isEditing} />
              {isEditing && errors.name && <p style={errorStyle}>{errors.name}</p>}
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>身份证号码</label>
              <input style={inputStyle} type="text" name="id_card_number" value={formData.id_card_number || ''} onChange={handleInputChange} readOnly={!isEditing} />
              {isEditing && errors.id_card_number && <p style={errorStyle}>{errors.id_card_number}</p>}
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>性别</label>
              <select style={inputStyle} name="gender" value={formData.gender || ''} onChange={handleInputChange} disabled={!isEditing}>
                <option>男</option>
                <option>女</option>
              </select>
            </div>
            <div style={formGroupStyle}><label style={labelStyle}>民族</label><input style={inputStyle} type="text" name="ethnicity" value={formData.ethnicity || ''} onChange={handleInputChange} readOnly={!isEditing} /></div>
          </div>

          <div style={formRowStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>工作部门</label>
              {isEditing ? (
                <select 
                  style={inputStyle} 
                  name="department" 
                  value={formData.department || ''} 
                  onChange={handleDepartmentChange}
                >
                  <option value="">请选择部门</option>
                  {deptOptions.map(option => (
                    <option key={option.id} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <input style={inputStyle} type="text" name="department" value={formData.department || ''} readOnly />
              )}
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>科室(工序)</label>
              {isEditing ? (
                <select 
                  style={inputStyle} 
                  name="section" 
                  value={formData.section || ''} 
                  onChange={handleSectionChange}
                  disabled={!formData.department}
                >
                  <option value="">请选择科室</option>
                  {sectionOptions.map(option => (
                    <option key={option.id} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <input style={inputStyle} type="text" name="section" value={formData.section || ''} readOnly />
              )}
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>班组</label>
              {isEditing ? (
                <select 
                  style={inputStyle} 
                  name="team" 
                  value={formData.team || ''} 
                  onChange={handleTeamChange}
                  disabled={!formData.section}
                >
                  <option value="">请选择班组</option>
                  {teamOptions.map(option => (
                    <option key={option.id} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <input style={inputStyle} type="text" name="team" value={formData.team || ''} readOnly />
              )}
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>工作岗位</label>
              {isEditing ? (
                <select 
                  style={inputStyle} 
                  name="position" 
                  value={formData.position || ''} 
                  onChange={handleInputChange}
                  disabled={!formData.team}
                >
                  <option value="">请选择岗位</option>
                  {positionOptions.map(option => (
                    <option key={option.id} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : (
                <input style={inputStyle} type="text" name="position" value={formData.position || ''} readOnly />
              )}
            </div>
          </div>
          
          <div style={formRowStyle}>
              <div style={formGroupStyle}><label style={labelStyle}>出生年月</label><input style={inputStyle} type="date" name="birth_date" value={formatDateForInput(formData.birth_date)} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}><label style={labelStyle}>政治面貌</label><input style={inputStyle} type="text" name="political_status" value={formData.political_status || ''} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}><label style={labelStyle}>学历</label><input style={inputStyle} type="text" name="education" value={formData.education || ''} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>婚姻状况</label>
                <select style={inputStyle} name="marital_status" value={formData.marital_status || ''} onChange={handleInputChange} disabled={!isEditing}>
                  <option>未婚</option>
                  <option>已婚</option>
                  <option>离异</option>
                  <option>丧偶</option>
                </select>
              </div>
          </div>

          <div style={formRowStyle}>
              <div style={formGroupStyle}><label style={labelStyle}>籍贯</label><input style={inputStyle} type="text" name="hometown" value={formData.hometown || ''} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}><label style={labelStyle}>家庭住址</label><input style={inputStyle} type="text" name="address" value={formData.address || ''} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>联系方式</label>
                <input style={inputStyle} type="text" name="phone" value={formData.phone || ''} onChange={handleInputChange} readOnly={!isEditing} />
                {isEditing && errors.phone && <p style={errorStyle}>{errors.phone}</p>}
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>邮箱</label>
                <input style={inputStyle} type="email" name="email" value={formData.email || ''} onChange={handleInputChange} readOnly={!isEditing} />
                {isEditing && errors.email && <p style={errorStyle}>{errors.email}</p>}
              </div>
          </div>

          <div style={formRowStyle}>
              <div style={formGroupStyle}><label style={labelStyle}>入职日期</label><input style={inputStyle} type="date" name="hire_date" value={formatDateForInput(formData.hire_date)} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}><label style={labelStyle}>合同到期日</label><input style={inputStyle} type="date" name="contract_due_date" value={formatDateForInput(formData.contract_due_date)} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}><label style={labelStyle}>合同终止日期</label><input style={inputStyle} type="date" name="contract_end_date" value={formatDateForInput(formData.contract_end_date)} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}><label style={labelStyle}>员工状态</label><input style={inputStyle} type="text" name="status" value={formData.status || ''} onChange={handleInputChange} readOnly={!isEditing} /></div>
          </div>

          <div style={formRowStyle}>
              <div style={formGroupStyle}><label style={labelStyle}>在岗体检时间</label><input style={inputStyle} type="date" name="on_the_job_medical_exam_date" value={formatDateForInput(formData.on_the_job_medical_exam_date)} onChange={handleInputChange} readOnly={!isEditing} /></div>
              <div style={formGroupStyle}>
                  <label style={labelStyle}>有无职业病史</label>
                  <select style={inputStyle} name="has_occupational_disease_history" value={formData.has_occupational_disease_history ? 'true' : 'false'} onChange={handleInputChange} disabled={!isEditing}>
                      <option value="false">无</option>
                      <option value="true">有</option>
                  </select>
              </div>
              <div style={formGroupStyle}><label style={labelStyle}>职业病史描述</label><textarea style={{...inputStyle, height: '80px'}} name="occupational_disease_history_description" value={formData.occupational_disease_history_description || ''} onChange={handleInputChange} readOnly={!isEditing}></textarea></div>
          </div>
        </form>
      ) : (view === 'details' && !employee && !detailError) ? null : (
        renderHistoryView()
      )}
    </Modal>
  );
};

export default EmployeeDetailModal;
